import { React, useEffect, useState } from "react";
import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import {
	parseKoreaDateTime,
	toKoreaApiDateTime,
} from "../../../../utilities/koreaTime";
import {
	Button,
	Card,
	ConfigProvider,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Layout,
	Modal,
	Select,
	Space,
	Table,
	Tabs,
	message,
} from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import AccessedCarNavigation from "../AccessedCarNavigation";
import {
	APPLICATION_KIND_OPERATION,
	APPLICATION_KIND_OPTIONS,
	APPLICATION_KIND_RENTAL,
	API_RENTAL_APPLY_TYPES,
	DRIVER_LICENSE_TYPE_OPTIONS,
	MAX_RENTAL_REQUESTED_COUNT,
	RENTAL_CAR_TYPE_OTHER,
	formatCarTypeLabel,
	getAssignableCarTypes,
	matchRequestedApplyTypeId,
} from "./rentalCarApplyUtils";
import useIsMobile from "../../../../hooks/useIsMobile";
import PageHeader from "../../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { Option } = Select;

const API_APPLICATION = "/utils/rental/car/apply/";
const OTHER_CAR_TYPE_NAME = "기타";
const HALF_HOUR_TIME_PICKER = { format: "HH:mm", minuteStep: 30 };

const formatPeriod = (record) =>
	`${parseKoreaDateTime(record.start_datetime).format("YYYY-MM-DD HH:mm")} ~ ${parseKoreaDateTime(
		record.end_datetime,
	).format("YYYY-MM-DD HH:mm")}`;

const isViewableOfficialDoc = (filename) =>
	/\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(filename || "");

const downloadOfficialDoc = async (
	applicationId,
	attachment,
	{ inline = false } = {},
) => {
	const res = await useAxios.get(
		`/utils/rental/car/apply/${applicationId}/attachments/${attachment.id}/download/`,
		{
			responseType: "blob",
			headers: { Accept: "application/octet-stream" },
			params: inline ? { inline: 1 } : undefined,
		},
	);
	const blob = new Blob([res.data]);
	const url = window.URL.createObjectURL(blob);
	if (inline) {
		window.open(url, "_blank", "noopener,noreferrer");
		return;
	}
	const a = document.createElement("a");
	a.href = url;
	a.download = attachment.original_filename || "download";
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
};

const ViewRentalCarApplications = () => {
	const isMobile = useIsMobile();
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("신청");
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [cancelModalVisible, setCancelModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [selectedApplication, setSelectedApplication] = useState(null);
	const [rejectReason, setRejectReason] = useState("");
	const [approvedCarTypeFilter, setApprovedCarTypeFilter] =
		useState(undefined);
	const [applyTypes, setApplyTypes] = useState([]);
	const [editForm] = Form.useForm();
	const [approveForm] = Form.useForm();
	const editApplyTypeId = Form.useWatch("requested_apply_type", editForm);
	const editApplicationKind = Form.useWatch("application_kind", editForm);
	const assignedCarsWatch =
		Form.useWatch("assigned_cars", approveForm) || [];

	useEffect(() => {
		fetchApplications();
		fetchApplyTypes();
	}, []);

	const fetchApplyTypes = async () => {
		try {
			const response = await useAxios.get(API_RENTAL_APPLY_TYPES);
			if (response.status === 200) {
				setApplyTypes(response.data.apply_types || []);
			}
		} catch (error) {
			message.error("차량 종류를 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const fetchApplications = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(API_APPLICATION);
			if (response.status === 200) {
				setApplications(
					response.data.rental_car_applications ||
						[],
				);
			}
		} catch (error) {
			message.error("신청 목록을 불러오는데 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const updateApplication = async (
		applicationId,
		payload,
		successText,
	) => {
		try {
			setLoading(true);
			const response = await useAxios.put(
				`${API_APPLICATION}${applicationId}/`,
				payload,
			);
			if (response.status === 200) {
				message.success(successText);
				fetchApplications();
				return true;
			}
		} catch (error) {
			const apiError = error.response?.data?.error;
			message.error(
				(typeof apiError === "string" && apiError) ||
					"처리 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
		return false;
	};

	const assignableCarTypes = getAssignableCarTypes(selectedApplication);
	const otherCarTypeId = assignableCarTypes.find(
		(type) => type.name === OTHER_CAR_TYPE_NAME,
	)?.id;
	const editApplyType = applyTypes.find(
		(type) => Number(type.id) === Number(editApplyTypeId),
	);

	const handleAccept = (record) => {
		setSelectedApplication(record);
		approveForm.resetFields();
		const count = Math.max(1, Number(record.requested_count) || 1);
		const matches = getAssignableCarTypes(record);
		approveForm.setFieldsValue({
			assigned_cars: Array.from({ length: count }, (_, index) => ({
				assigned_car_type: matches[index]?.id,
				assigned_custom_car_number: "",
			})),
		});
		setApproveModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!selectedApplication) {
			return;
		}
		let values;
		try {
			values = await approveForm.validateFields();
		} catch {
			message.error("입력 정보를 확인해주세요.");
			return;
		}

		const isOperation =
			selectedApplication.application_kind ===
			APPLICATION_KIND_OPERATION;
		const assignedCars = (values.assigned_cars || []).map(
			(item) => ({
				assigned_car_type: item.assigned_car_type,
				assigned_custom_car_number:
					Number(item.assigned_car_type) ===
					Number(otherCarTypeId)
						? item.assigned_custom_car_number?.trim() ||
							""
						: "",
			}),
		);
		const succeeded = await updateApplication(
			selectedApplication.id,
			{
				status: "승인",
				assigned_cars: assignedCars,
				assigned_driver: isOperation
					? values.assigned_driver?.trim() || ""
					: undefined,
			},
			"차량 대여 신청이 승인되어 일정에 등록되었습니다.",
		);
		if (succeeded) {
			setApproveModalVisible(false);
			approveForm.resetFields();
		}
	};

	const handleReject = (record) => {
		setSelectedApplication(record);
		setRejectReason("");
		setRejectModalVisible(true);
	};

	const handleRejectConfirm = async () => {
		if (!rejectReason.trim()) {
			message.error("거절 사유를 입력해주세요.");
			return;
		}
		const succeeded = await updateApplication(
			selectedApplication.id,
			{ status: "거절", reject_reason: rejectReason.trim() },
			"신청이 거절되었습니다.",
		);
		if (succeeded) {
			setRejectModalVisible(false);
			setRejectReason("");
		}
	};

	const handleCancel = (record) => {
		setSelectedApplication(record);
		setCancelModalVisible(true);
	};

	const handleCancelConfirm = async () => {
		const succeeded = await updateApplication(
			selectedApplication.id,
			{ status: "거절", reject_reason: "예약 취소" },
			"예약이 취소되었습니다.",
		);
		if (succeeded) {
			setCancelModalVisible(false);
		}
	};

	const handleEdit = (record) => {
		setSelectedApplication(record);
		editForm.setFieldsValue({
			start_datetime: parseKoreaDateTime(
				record.start_datetime,
			),
			end_datetime: parseKoreaDateTime(record.end_datetime),
			application_kind:
				record.application_kind ||
				APPLICATION_KIND_RENTAL,
			title: record.title,
			route: record.route || "",
			driver: record.driver,
			driver_license_type: record.driver_license_type || undefined,
			applicant_name: record.applicant_name,
			applicant_contact: record.applicant_contact,
			requested_apply_type: matchRequestedApplyTypeId(
				record,
				applyTypes,
			),
			custom_car_type: record.custom_car_type || "",
			requested_count: record.requested_count || 1,
		});
		setEditModalVisible(true);
	};

	const handleEditConfirm = async () => {
		let values;
		try {
			values = await editForm.validateFields();
		} catch {
			message.error("입력 정보를 확인해주세요.");
			return;
		}

		if (!values.end_datetime.isAfter(values.start_datetime)) {
			message.error(
				"대여 마감 일시는 시작 일시보다 이후여야 합니다.",
			);
			return;
		}

		const selectedType = applyTypes.find(
			(type) =>
				Number(type.id) ===
				Number(values.requested_apply_type),
		);
		const succeeded = await updateApplication(
			selectedApplication.id,
			{
				start_datetime: toKoreaApiDateTime(
					values.start_datetime,
				),
				end_datetime: toKoreaApiDateTime(
					values.end_datetime,
				),
				application_kind: values.application_kind,
				title: values.title,
				route: values.route,
				driver:
					values.application_kind ===
					APPLICATION_KIND_OPERATION
						? ""
						: values.driver,
				driver_license_type:
					values.application_kind ===
					APPLICATION_KIND_OPERATION
						? ""
						: values.driver_license_type,
				applicant_name: values.applicant_name,
				applicant_contact: values.applicant_contact,
				requested_apply_type: values.requested_apply_type,
				custom_car_type:
					selectedType?.name ===
					RENTAL_CAR_TYPE_OTHER
						? values.custom_car_type
						: "",
				requested_count: selectedType?.is_multiple
					? Number(values.requested_count) || 1
					: 1,
			},
			"신청 내용이 수정되었습니다.",
		);
		if (succeeded) {
			setEditModalVisible(false);
			editForm.resetFields();
		}
	};

	const getColumns = (status) => {
		const baseColumns = [
			{
				title: "구분",
				dataIndex: "application_kind",
				key: "application_kind",
				filters: [
					{ text: "대여", value: APPLICATION_KIND_RENTAL },
					{ text: "운행", value: APPLICATION_KIND_OPERATION },
				],
				onFilter: (value, record) =>
					(record.application_kind || APPLICATION_KIND_RENTAL) ===
					value,
				render: (value) => {
					if (value === APPLICATION_KIND_OPERATION) return "운행";
					if (value === APPLICATION_KIND_RENTAL) return "대여";
					return value || "대여";
				},
			},
			{
				title: "차량 종류",
				key: "car_type",
				render: (_, record) =>
					formatCarTypeLabel(record),
			},
			{
				title: "대여 일시",
				key: "period",
				render: (_, record) => formatPeriod(record),
			},
			{
				title: "대여 내역",
				dataIndex: "title",
				key: "title",
			},
			{
				title: "운행경로",
				dataIndex: "route",
				key: "route",
				render: (value) => value || "-",
			},
			{
				title: "운전자",
				dataIndex: "driver",
				key: "driver",
				render: (value) => value || "-",
			},
			{
				title: "운전자 면허",
				dataIndex: "driver_license_type",
				key: "driver_license_type",
				render: (value) => value || "-",
			},
			{
				title: "신청자",
				dataIndex: "applicant_name",
				key: "applicant_name",
			},
			{
				title: "신청자 연락처",
				dataIndex: "applicant_contact",
				key: "applicant_contact",
			},
			{
				title: "공문/협조문",
				key: "attachments",
				render: (_, record) => {
					const attachments =
						record.attachments || [];
					if (!attachments.length) {
						return "-";
					}
					return (
						<Space
							direction="vertical"
							size={4}>
							{attachments.map(
								(att) => (
									<Space
										key={
											att.id
										}
										size={
											4
										}
										wrap>
										<span>
											{
												att.original_filename
											}
										</span>
										{isViewableOfficialDoc(
											att.original_filename,
										) ? (
											<Button
												type="link"
												size="small"
												icon={
													<EyeOutlined />
												}
												onClick={() =>
													downloadOfficialDoc(
														record.id,
														att,
														{
															inline: true,
														},
													).catch(
														() =>
															message.error(
																"파일을 열 수 없습니다.",
															),
													)
												}>
												보기
											</Button>
										) : null}
										<Button
											type="link"
											size="small"
											icon={
												<DownloadOutlined />
											}
											onClick={() =>
												downloadOfficialDoc(
													record.id,
													att,
												).catch(
													() =>
														message.error(
															"파일을 받을 수 없습니다.",
														),
												)
											}>
											받기
										</Button>
									</Space>
								),
							)}
						</Space>
					);
				},
			},
			{
				title: "신청일",
				dataIndex: "created_at",
				key: "created_at",
				render: (date) =>
					parseKoreaDateTime(date).format(
						"YYYY-MM-DD HH:mm",
					),
			},
		];

		if (status === "신청") {
			baseColumns.push({
				title: "관리",
				key: "action",
				render: (_, record) => (
					<Space size="middle">
						<Button
							type="primary"
							onClick={() =>
								handleAccept(
									record,
								)
							}>
							승인
						</Button>
						<Button
							danger
							onClick={() =>
								handleReject(
									record,
								)
							}>
							거절
						</Button>
					</Space>
				),
			});
		}

		if (status === "승인") {
			baseColumns.push({
				title: "관리",
				key: "action",
				render: (_, record) => (
					<Space size="middle">
						<Button
							type="primary"
							onClick={() =>
								handleEdit(
									record,
								)
							}>
							수정
						</Button>
						<Button
							danger
							onClick={() =>
								handleCancel(
									record,
								)
							}>
							예약 취소
						</Button>
					</Space>
				),
			});
		}

		if (status === "거절") {
			baseColumns.push({
				title: "거절 사유",
				dataIndex: "reject_reason",
				key: "reject_reason",
			});
		}

		return baseColumns;
	};

	const approvedApplications = applications.filter(
		(app) => app.status === "승인",
	);

	const approvedCarTypeOptions = [
		...new Set(
			approvedApplications
				.map((app) => formatCarTypeLabel(app))
				.filter(Boolean),
		),
	];

	const filteredApprovedApplications = approvedApplications.filter(
		(app) =>
			!approvedCarTypeFilter ||
			formatCarTypeLabel(app) === approvedCarTypeFilter,
	);

	const items = [
		{
			key: "신청",
			label: "신청",
			children: (
				<Table
					columns={getColumns("신청")}
					dataSource={applications.filter(
						(app) => app.status === "신청",
					)}
					rowKey="id"
					loading={loading}
					scroll={{ x: "max-content" }}
					sticky={{ offsetHeader: 64 }}
					pagination={{ pageSize: 10 }}
				/>
			),
		},
		{
			key: "승인",
			label: "승인",
			children: (
				<>
					<Space
						size="middle"
						wrap
						style={{ marginBottom: 16 }}>
						<Select
							allowClear
							style={{
								minWidth: 180,
							}}
							placeholder="차량 종류 선택"
							value={
								approvedCarTypeFilter
							}
							onChange={(value) =>
								setApprovedCarTypeFilter(
									value,
								)
							}>
							{approvedCarTypeOptions.map(
								(carType) => (
									<Option
										key={
											carType
										}
										value={
											carType
										}>
										{
											carType
										}
									</Option>
								),
							)}
						</Select>
					</Space>
					<Table
						columns={getColumns("승인")}
						dataSource={
							filteredApprovedApplications
						}
						rowKey="id"
						loading={loading}
						scroll={{ x: "max-content" }}
						sticky={{ offsetHeader: 64 }}
						pagination={{ pageSize: 10 }}
					/>
				</>
			),
		},
		{
			key: "거절",
			label: "거절",
			children: (
				<Table
					columns={getColumns("거절")}
					dataSource={applications.filter(
						(app) => app.status === "거절",
					)}
					rowKey="id"
					loading={loading}
					scroll={{ x: "max-content" }}
					sticky={{ offsetHeader: 64 }}
					pagination={{ pageSize: 10 }}
				/>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="차량 대여 신청 관리" />
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader title="차량 대여 신청 관리" />
							<div className="utils-rent_sanctuary__content-container">
								<Card className="surface-card utils-rent_sanctuary__table-card">
									<Tabs
										activeKey={
											activeTab
										}
										onChange={
											setActiveTab
										}
										items={
											items
										}
									/>
								</Card>
							</div>
						</div>

						<Modal
							title="신청 승인"
							open={
								approveModalVisible
							}
							onOk={
								handleApproveConfirm
							}
							onCancel={() => {
								setApproveModalVisible(
									false,
								);
								approveForm.resetFields();
							}}
							okText="승인"
							cancelText="취소"
							width={
								(selectedApplication?.requested_count ||
									1) > 1
									? 560
									: 520
							}
							okButtonProps={{
								loading: loading,
								disabled:
									assignableCarTypes.length ===
									0,
							}}>
							{selectedApplication && (
								<div
									style={{
										marginBottom: 16,
									}}>
									<p>
										<strong>
											대여
											종류:
										</strong>{" "}
										{
											selectedApplication.application_kind
										}
									</p>
									<p>
										<strong>
											신청
											차량
											종류:
										</strong>{" "}
										{formatCarTypeLabel(
											selectedApplication,
										)}
									</p>
									<p>
										<strong>
											운행경로:
										</strong>{" "}
										{selectedApplication.route ||
											"-"}
									</p>
									<p>
										<strong>
											대여
											일시:
										</strong>{" "}
										{formatPeriod(
											selectedApplication,
										)}
									</p>
									<p>
										<strong>
											대여
											내역:
										</strong>{" "}
										{
											selectedApplication.title
										}
									</p>
									{selectedApplication.driver ? (
										<p>
											<strong>
												신청
												운전자:
											</strong>{" "}
											{
												selectedApplication.driver
											}
											{selectedApplication.driver_license_type
												? ` (${selectedApplication.driver_license_type})`
												: ""}
										</p>
									) : null}
								</div>
							)}
							<Form
								form={
									approveForm
								}
								layout="vertical">
								{assignableCarTypes.length ===
								0 ? (
									<p
										style={{
											color: "#ff4d4f",
											marginBottom: 12,
										}}>
										이 신청
										차종에
										매칭된
										배정
										차량이
										없습니다.
										관리자에서
										신청
										차종
										매칭을
										지정해주세요.
									</p>
								) : null}
								<Form.List name="assigned_cars">
									{(
										fields,
									) =>
										fields.map(
											(
												field,
												index,
											) => (
												<div
													key={
														field.key
													}>
													<Form.Item
														{...field}
														name={[
															field.name,
															"assigned_car_type",
														]}
														label={
															fields.length >
															1
																? `배정 차량 ${index + 1}`
																: "실제 배정 차량 종류"
														}
														rules={[
															{
																required: true,
																message: "차량 종류를 선택해주세요",
															},
														]}>
														<Select placeholder="매칭된 차량을 선택하세요">
															{assignableCarTypes.map(
																(
																	type,
																) => (
																	<Option
																		key={
																			type.id
																		}
																		value={
																			type.id
																		}>
																		{
																			type.name
																		}
																	</Option>
																),
															)}
														</Select>
													</Form.Item>
													{Number(
														assignedCarsWatch[
															index
														]
															?.assigned_car_type,
													) ===
														Number(
															otherCarTypeId,
														) &&
														otherCarTypeId !=
															null && (
															<Form.Item
																name={[
																	field.name,
																	"assigned_custom_car_number",
																]}
																label="차량 번호"
																rules={[
																	{
																		required: true,
																		message: "차량 번호를 입력하세요.",
																	},
																]}>
																<Input placeholder="차량 번호를 입력하세요" />
															</Form.Item>
														)}
												</div>
											),
										)
									}
								</Form.List>
								{(selectedApplication?.requested_count ||
									1) > 1 ? (
									<p
										style={{
											color: "#666",
											marginTop: -4,
											marginBottom: 12,
										}}>
										신청
										대수만큼
										차량을
										지정하면
										일정이
										한꺼번에
										등록됩니다.
									</p>
								) : null}
								{selectedApplication?.application_kind ===
									APPLICATION_KIND_OPERATION && (
									<Form.Item
										name="assigned_driver"
										label="운전자"
										rules={[
											{
												required: true,
												message: "운전자 이름을 입력해주세요",
											},
										]}>
										<Input placeholder="운전자 이름을 입력하세요" />
									</Form.Item>
								)}
							</Form>
						</Modal>

						<Modal
							title="신청 거절"
							open={
								rejectModalVisible
							}
							onOk={
								handleRejectConfirm
							}
							onCancel={() => {
								setRejectModalVisible(
									false,
								);
								setRejectReason(
									"",
								);
							}}
							okText="거절"
							cancelText="취소"
							okButtonProps={{
								danger: true,
								loading: loading,
							}}>
							<p>
								거절 사유를
								입력해주세요:
							</p>
							<Input.TextArea
								rows={4}
								value={
									rejectReason
								}
								onChange={(e) =>
									setRejectReason(
										e
											.target
											.value,
									)
								}
								placeholder="거절 사유를 입력하세요"
							/>
						</Modal>

						<Modal
							title="예약 취소 확인"
							open={
								cancelModalVisible
							}
							onOk={
								handleCancelConfirm
							}
							onCancel={() =>
								setCancelModalVisible(
									false,
								)
							}
							okText="예약 취소"
							cancelText="취소"
							okButtonProps={{
								danger: true,
								loading: loading,
							}}>
							<p>
								정말로 이 예약을
								취소하시겠습니까?
							</p>
							{selectedApplication && (
								<div
									style={{
										marginTop: 16,
									}}>
									<p>
										<strong>
											차량
											종류:
										</strong>{" "}
										{formatCarTypeLabel(
											selectedApplication,
										)}
									</p>
									<p>
										<strong>
											대여
											일시:
										</strong>{" "}
										{formatPeriod(
											selectedApplication,
										)}
									</p>
									<p>
										<strong>
											대여
											내역:
										</strong>{" "}
										{
											selectedApplication.title
										}
									</p>
									<p>
										<strong>
											운행경로:
										</strong>{" "}
										{selectedApplication.route ||
											"-"}
									</p>
								</div>
							)}
							<p
								style={{
									marginTop: 16,
									color: "#ff4d4f",
								}}>
								예약 취소 시
								상태가 '거절'로
								변경되고, 사유는
								'예약 취소'로
								기록됩니다.
							</p>
						</Modal>

						<Modal
							title="신청 내용 수정"
							open={editModalVisible}
							onOk={handleEditConfirm}
							onCancel={() => {
								setEditModalVisible(
									false,
								);
								editForm.resetFields();
							}}
							okText="수정"
							cancelText="취소"
							width={600}
							okButtonProps={{
								loading: loading,
							}}>
							<Form
								form={editForm}
								layout="vertical">
								<Form.Item
									name="start_datetime"
									label="대여 시작 일시"
									rules={[
										{
											required: true,
											message: "대여 시작 일시를 선택해주세요",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										showTime={
											HALF_HOUR_TIME_PICKER
										}
										allowClear={
											false
										}
									/>
								</Form.Item>
								<Form.Item
									name="end_datetime"
									label="대여 마감 일시"
									rules={[
										{
											required: true,
											message: "대여 마감 일시를 선택해주세요",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										showTime={
											HALF_HOUR_TIME_PICKER
										}
										allowClear={
											false
										}
									/>
								</Form.Item>
								<Form.Item
									name="application_kind"
									label="대여 종류"
									rules={[
										{
											required: true,
											message: "대여 종류를 선택해주세요",
										},
									]}>
									<Select placeholder="대여 종류를 선택하세요">
										{APPLICATION_KIND_OPTIONS.map(
											(
												kind,
											) => (
												<Option
													key={
														kind
													}
													value={
														kind
													}>
													{
														kind
													}
												</Option>
											),
										)}
									</Select>
								</Form.Item>
								<Form.Item
									name="title"
									label="대여 내역"
									rules={[
										{
											required: true,
											message: "대여 내역을 입력해주세요",
										},
									]}>
									<Input placeholder="대여 내역을 입력하세요" />
								</Form.Item>
								<Form.Item
									name="route"
									label="운행경로"
									rules={[
										{
											required: true,
											message: "운행경로를 입력해주세요",
										},
									]}>
									<Input placeholder="예: 본부 → 수련원 → 본부" />
								</Form.Item>
								{editApplicationKind !==
									APPLICATION_KIND_OPERATION && (
									<>
										<Form.Item
											name="driver"
											label="운전자 이름"
											rules={[
												{
													required: true,
													message: "운전자 이름을 입력해주세요",
												},
											]}>
											<Input placeholder="운전자 이름을 입력하세요" />
										</Form.Item>
										<Form.Item
											name="driver_license_type"
											label="운전자 면허 종류"
											rules={[
												{
													required: true,
													message: "운전자 면허 종류를 선택해주세요",
												},
											]}>
											<Select placeholder="면허 종류를 선택하세요">
												{DRIVER_LICENSE_TYPE_OPTIONS.map(
													(
														licenseType,
													) => (
														<Option
															key={
																licenseType
															}
															value={
																licenseType
															}>
															{
																licenseType
															}
														</Option>
													),
												)}
											</Select>
										</Form.Item>
									</>
								)}
								<Form.Item
									name="applicant_name"
									label="신청자 이름"
									rules={[
										{
											required: true,
											message: "신청자 이름을 입력해주세요",
										},
									]}>
									<Input placeholder="신청자 이름을 입력하세요" />
								</Form.Item>
								<Form.Item
									name="applicant_contact"
									label="신청자 연락처"
									rules={[
										{
											required: true,
											message: "신청자 연락처를 입력해주세요",
										},
										{
											pattern: /^[0-9-]+$/,
											message: "숫자와 하이픈(-)만 입력 가능합니다",
										},
									]}>
									<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
								</Form.Item>
								<Form.Item
									name="requested_apply_type"
									label="차량 종류"
									rules={[
										{
											required: true,
											message: "차량 종류를 선택해주세요",
										},
									]}>
									<Select
										placeholder="차량 종류를 선택하세요"
										onChange={(
											value,
										) => {
											const type =
												applyTypes.find(
													(
														item,
													) =>
														Number(
															item.id,
														) ===
														Number(
															value,
														),
												);
											if (
												!type?.is_multiple
											) {
												editForm.setFieldsValue(
													{
														requested_count: 1,
													},
												);
											}
										}}>
										{applyTypes.map(
											(
												carType,
											) => (
												<Option
													key={
														carType.id
													}
													value={
														carType.id
													}>
													{
														carType.name
													}
													{carType.is_multiple
														? " (복수 대여)"
														: ""}
												</Option>
											),
										)}
									</Select>
								</Form.Item>
								{editApplyType?.name ===
									RENTAL_CAR_TYPE_OTHER && (
									<Form.Item
										name="custom_car_type"
										label="차량 종류 직접 입력"
										rules={[
											{
												required: true,
												message: "차량 종류를 입력해주세요",
											},
										]}>
										<Input placeholder="차량 종류를 입력하세요" />
									</Form.Item>
								)}
								{editApplyType?.is_multiple && (
									<Form.Item
										name="requested_count"
										label="차량 대수"
										rules={[
											{
												required: true,
												message: "차량 대수를 입력해주세요",
											},
										]}>
										<InputNumber
											min={
												1
											}
											max={
												MAX_RENTAL_REQUESTED_COUNT
											}
											style={{
												width: "100%",
											}}
										/>
									</Form.Item>
								)}
							</Form>
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ViewRentalCarApplications;
